import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Job } from "@/types/job.types";
import type { Database } from "@/integrations/supabase/types";
import type { JobFilters } from "@/components/jobs/filters/JobFiltersModal";

type JobWithCompany = Database['public']['Tables']['jobs']['Row'] & {
  companies: Database['public']['Tables']['companies']['Row'] | null;
};

async function fetchJobs(searchQuery?: string): Promise<Job[]> {
  // 1. Buscar vagas curadas
  const { data: curatedData } = await supabase
    .from('curated_jobs')
    .select('*')
    .eq('status', 'active')
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  // 2. Buscar vagas normais
  let query = supabase
    .from('jobs')
    .select(`
      *,
      companies (
        name,
        location,
        logo_url
      )
    `)
    .eq('status', 'active')
    .is('deleted_at', null);

  if (searchQuery) {
    query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message);
  }

  // 3. Transformar vagas curadas
  const curatedJobs: Job[] = (curatedData || []).map(job => ({
    id: job.id,
    title: job.title,
    company: job.company_name,
    location: 'Ver detalhes',
    type: job.work_model || 'Não especificado',
    description: job.description,
    seniority: 'Não especificado',
    salary_range: 'A combinar',
    contract_type: 'Não especificado',
    application_url: job.external_link,
    logo_url: undefined,
    benefits: undefined,
    requirements: [],
    responsibilities: [],
    views: 0,
    applications: 0,
    isCurated: true,
    isFeatured: false
  }));

  // 4. Formatar vagas normais
  const regularJobs: Job[] = ((data as JobWithCompany[]) || []).map(job => ({
    id: job.id,
    title: job.title,
    company: job.companies?.name || 'Empresa não especificada',
    location: job.companies?.location || 'Localização não especificada',
    type: job.work_model || 'Não especificado',
    logo_url: job.companies?.logo_url,
    description: job.description,
    seniority: job.experience_level || 'Não especificado',
    salary_range: job.salary_range || 'A combinar',
    contract_type: job.contract_type || 'Não especificado',
    application_url: job.external_link,
    benefits: job.benefits ? JSON.parse(job.benefits as string) : undefined,
    requirements: job.requirements || [],
    responsibilities: job.responsibilities || [],
    views: job.views_count || 0,
    applications: job.applications_count || 0,
    isFeatured: (job as any).is_featured || false
  }));

  const allJobs = [...curatedJobs, ...regularJobs];
  // Featured jobs first
  allJobs.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
  return allJobs;
}

function applyFilters(jobs: Job[], filters?: JobFilters): Job[] {
  if (!filters) return jobs;

  return jobs.filter(job => {
    // Filtro por tipo de trabalho
    if (filters.workType.length > 0) {
      const jobWorkType = job.type.toLowerCase();
      const matchesWorkType = filters.workType.some(filter => {
        const filterLower = filter.toLowerCase();
        return jobWorkType.includes(filterLower) || 
               (filterLower === 'remoto' && (jobWorkType.includes('remote') || jobWorkType.includes('remoto'))) ||
               (filterLower === 'hibrido' && (jobWorkType.includes('híbrido') || jobWorkType.includes('hybrid'))) ||
               (filterLower === 'presencial' && (jobWorkType.includes('presencial') || jobWorkType.includes('on-site') || jobWorkType.includes('onsite')));
      });
      if (!matchesWorkType) return false;
    }

    // Filtro por tipo de contrato
    if (filters.contractType.length > 0) {
      const jobContractType = job.contract_type.toLowerCase();
      const matchesContract = filters.contractType.some(filter => {
        const filterLower = filter.toLowerCase();
        return jobContractType.includes(filterLower) ||
               (filterLower === 'clt' && jobContractType.includes('clt')) ||
               (filterLower === 'pj' && (jobContractType.includes('pj') || jobContractType.includes('pessoa jurídica'))) ||
               (filterLower === 'freelancer' && (jobContractType.includes('freelancer') || jobContractType.includes('free')));
      });
      if (!matchesContract) return false;
    }

    // Filtro por senioridade
    if (filters.seniority.length > 0) {
      const jobSeniority = job.seniority.toLowerCase();
      const matchesSeniority = filters.seniority.some(filter => {
        const filterLower = filter.toLowerCase();
        return jobSeniority.includes(filterLower) ||
               (filterLower === 'junior' && (jobSeniority.includes('júnior') || jobSeniority.includes('jr') || jobSeniority.includes('estagiário') || jobSeniority.includes('trainee'))) ||
               (filterLower === 'pleno' && (jobSeniority.includes('pleno') || jobSeniority.includes('mid'))) ||
               (filterLower === 'senior' && (jobSeniority.includes('sênior') || jobSeniority.includes('sr') || jobSeniority.includes('senior')));
      });
      if (!matchesSeniority) return false;
    }

    // Filtro por faixa salarial
    if (filters.salaryRanges.length > 0) {
      const salary = job.salary_range.toLowerCase();
      const salaryNumbers = salary.match(/\d+/g);
      
      const matchesSalary = filters.salaryRanges.some(range => {
        if (range === "0-3000") {
          return salaryNumbers && salaryNumbers.some(num => {
            const value = parseInt(num);
            return value <= 3000 && value >= 1000;
          });
        }
        if (range === "3000-6000") {
          return salaryNumbers && salaryNumbers.some(num => {
            const value = parseInt(num);
            return value >= 3000 && value <= 6000;
          });
        }
        if (range === "6000-10000") {
          return salaryNumbers && salaryNumbers.some(num => {
            const value = parseInt(num);
            return value >= 6000 && value <= 10000;
          });
        }
        if (range === "10000+") {
          return salaryNumbers && salaryNumbers.some(num => {
            const value = parseInt(num);
            return value >= 10000;
          });
        }
        return false;
      });
      if (!matchesSalary) return false;
    }

    // Filtro por tags de área de dados
    if (filters.dataTags.length > 0) {
      const jobContent = `${job.title} ${job.description}`.toLowerCase();
      const matchesTags = filters.dataTags.some(tag => {
        switch(tag) {
          case 'data-analyst':
            return jobContent.includes('analista') || jobContent.includes('analyst') || jobContent.includes('análise');
          case 'data-scientist':
            return jobContent.includes('cientista') || jobContent.includes('scientist') || jobContent.includes('ciência');
          case 'data-engineer':
            return jobContent.includes('engenheiro') || jobContent.includes('engineer') || jobContent.includes('engenharia');
          case 'bi':
            return jobContent.includes('bi') || jobContent.includes('business intelligence') || jobContent.includes('power bi');
          case 'python':
            return jobContent.includes('python');
          case 'sql':
            return jobContent.includes('sql') || jobContent.includes('mysql') || jobContent.includes('postgresql');
          case 'ml':
            return jobContent.includes('machine learning') || jobContent.includes('ml') || jobContent.includes('aprendizado');
          case 'etl':
            return jobContent.includes('etl') || jobContent.includes('extract') || jobContent.includes('pipeline');
          case 'analytics':
            return jobContent.includes('analytics') || jobContent.includes('analítica') || jobContent.includes('web analytics');
          default:
            return jobContent.includes(tag.replace('-', ' '));
        }
      });
      if (!matchesTags) return false;
    }

    return true;
  });
}

export function useJobsQuery(searchQuery?: string, filters?: JobFilters) {
  const query = useQuery({
    queryKey: ['jobs', searchQuery],
    queryFn: () => fetchJobs(searchQuery),
    staleTime: 1000 * 60 * 3, // 3 minutos
  });

  const filteredJobs = query.data ? applyFilters(query.data, filters) : [];

  return {
    ...query,
    jobs: filteredJobs,
  };
}
